import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingBag, Menu, X, User, Phone, LogIn, UserPlus, LayoutDashboard, LogOut } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { EggmanLogo } from "./EggmanLogo";

const navItems = [
  { to: "/", label: "Home" },
  { to: "/products", label: "Products" },
  { to: "/wholesale", label: "Wholesale" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const dashHome =
    user?.role === "admin" ? "/admin/dashboard" : user?.role === "driver" ? "/driver/dashboard" : "/customer/dashboard";

  return (
    <header className="sticky top-0 z-40 bg-[#FAFAF9]/90 backdrop-blur-md border-b border-stone-200" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10 lg:px-12 h-16 md:h-24 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="shrink-0">
          <EggmanLogo className="h-11 md:h-16" />
        </Link>

        <nav className="hidden md:flex items-center gap-9">
          {navItems.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.to === "/"}
              data-testid={`nav-link-${it.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm font-semibold transition-colors ${
                  it.highlight
                    ? (isActive ? "text-[#EA580C]" : "text-[#EA580C] hover:text-[#c2410c]")
                    : (isActive ? "text-[#166534]" : "text-stone-600 hover:text-stone-900")
                }`
              }
            >
              {it.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <Link
            to="/cart"
            data-testid="nav-cart-btn"
            className="relative p-2.5 rounded-full border border-stone-200 hover:border-[#166534] transition-colors"
            aria-label="Cart"
          >
            <ShoppingBag size={18} className="text-stone-700" />
            {count > 0 && (
              <span
                data-testid="cart-count-badge"
                className="absolute -top-1.5 -right-1.5 bg-[#EA580C] text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center"
              >
                {count}
              </span>
            )}
          </Link>

          {user ? (
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate(dashHome)}
                data-testid="nav-dashboard-btn"
                className="px-4 py-2 rounded-full bg-[#166534] text-white text-sm font-semibold hover:bg-[#14532d] transition-colors flex items-center gap-2"
              >
                <User size={14} />
                Dashboard
              </button>
              <button
                onClick={() => {
                  logout();
                  navigate("/");
                }}
                data-testid="nav-logout-btn"
                className="text-sm font-semibold text-stone-600 hover:text-stone-900 px-3"
              >
                Logout
              </button>
            </div>
          ) : (
            <>
              <Link
                to="/login"
                data-testid="nav-login-link"
                className="text-sm font-semibold text-stone-700 hover:text-stone-900 px-3"
              >
                Login
              </Link>
              <Link
                to="/signup"
                data-testid="nav-signup-link"
                className="text-sm font-semibold text-[#166534] hover:text-[#14532d] border border-[#166534] rounded-full px-4 py-2"
              >
                Sign Up
              </Link>
              <Link
                to="/products"
                data-testid="nav-order-cta"
                className="bg-[#EA580C] text-white rounded-full font-bold px-5 py-2.5 text-sm hover:bg-[#c2410c] transition-colors shadow-sm hover:shadow"
              >
                Order Now
              </Link>
              <a
                href="tel:0223734083"
                data-testid="nav-phone"
                className="hidden lg:inline-flex items-center gap-1.5 text-sm font-semibold text-[#0E2818] hover:text-[#EA580C] transition-colors"
              >
                <span aria-hidden="true">📞</span>022 373 4083
              </a>
            </>
          )}
        </div>

        <div className="flex items-center gap-2 md:hidden">
          <Link
            to="/cart"
            data-testid="nav-mobile-cart-btn"
            className="relative p-2.5 rounded-full border border-stone-200"
            aria-label="Cart"
          >
            <ShoppingBag size={18} className="text-stone-700" />
            {count > 0 && (
              <span className="absolute -top-1 -right-1 bg-[#EA580C] text-white text-[10px] font-bold rounded-full w-4.5 h-4.5 min-w-[18px] h-[18px] flex items-center justify-center">
                {count}
              </span>
            )}
          </Link>
          <button
            className="p-2.5 rounded-full border border-stone-200"
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer — app-style, slides from top */}
      {open && (
        <>
          <div className="md:hidden fixed inset-0 bg-stone-900/40 backdrop-blur-sm z-30" onClick={() => setOpen(false)} />
          <div className="md:hidden absolute left-0 right-0 top-full bg-white border-t border-stone-200 shadow-2xl z-40 rounded-b-3xl overflow-hidden" data-testid="nav-mobile-panel">
            <div className="px-5 py-5 space-y-1">
              {navItems.map((it) => (
                <NavLink
                  key={it.to}
                  to={it.to}
                  end={it.to === "/"}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${it.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `block px-4 py-3.5 rounded-xl text-base font-semibold transition-colors ${isActive ? "bg-green-50 text-[#166534]" : "text-stone-800 hover:bg-stone-50"}`
                  }
                >
                  {it.label}
                </NavLink>
              ))}

              <div className="border-t border-stone-100 my-3" />

              {user ? (
                <>
                  <Link
                    to={dashHome}
                    onClick={() => setOpen(false)}
                    data-testid="mobile-nav-dashboard"
                    className="flex items-center gap-3 px-4 py-3.5 rounded-xl bg-[#166534] text-white font-bold"
                  >
                    <LayoutDashboard size={18} /> Dashboard
                  </Link>
                  <button
                    onClick={() => {
                      logout();
                      setOpen(false);
                      navigate("/");
                    }}
                    data-testid="mobile-nav-logout"
                    className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-left text-stone-700 font-semibold hover:bg-stone-50"
                  >
                    <LogOut size={18} /> Logout
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    onClick={() => setOpen(false)}
                    data-testid="mobile-nav-login"
                    className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-stone-800 font-semibold border border-stone-200"
                  >
                    <LogIn size={18} /> Login
                  </Link>
                  <Link
                    to="/signup"
                    onClick={() => setOpen(false)}
                    data-testid="mobile-nav-signup"
                    className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-[#166534] font-semibold border-2 border-[#166534]"
                  >
                    <UserPlus size={18} /> Sign up
                  </Link>
                  <Link
                    to="/products"
                    onClick={() => setOpen(false)}
                    data-testid="mobile-nav-order"
                    className="flex items-center justify-center gap-2 px-4 py-3.5 rounded-xl bg-[#EA580C] text-white font-bold shadow-md"
                  >
                    Order Now
                  </Link>
                </>
              )}

              <a
                href="tel:0223734083"
                onClick={() => setOpen(false)}
                data-testid="mobile-nav-phone"
                className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-stone-700 font-semibold hover:bg-stone-50"
              >
                <Phone size={18} className="text-[#EA580C]" /> 022 373 4083
              </a>
            </div>
          </div>
        </>
      )}
    </header>
  );
}
